import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ChevronRight,
  Plus,
  Play,
  Edit,
  Copy,
  Search,
  FileCode,
  Check,
  X,
} from 'lucide-react';
import {
  usePipelineDefinitions,
  useStartPipeline,
  StatusBadge,
  ActionButton,
  JsonViewer,
  type PipelineDefinition,
} from '@/components/hermes';
import { toast } from 'sonner';

export const Route = createFileRoute('/hermes/definitions')({
  component: DefinitionsPage,
});

function DefinitionsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [runModal, setRunModal] = useState<PipelineDefinition | null>(null);

  const { data: definitions, isLoading } = usePipelineDefinitions();

  const filteredDefinitions = definitions?.filter((def) => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!def.name.toLowerCase().includes(search) &&
          !def.description?.toLowerCase().includes(search)) {
        return false;
      }
    }
    // Active filter
    if (activeFilter === 'active' && !def.is_active) return false;
    if (activeFilter === 'inactive' && def.is_active) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <Link to="/hermes" className="hover:text-foreground">Hermes</Link>
            <ChevronRight className="w-4 h-4" />
            <span>Definitions</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground">Pipeline Definitions</h1>
        </div>
        <Link
          to="/hermes/builder"
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Pipeline
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search pipelines..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>

        <div className="flex gap-1 bg-secondary rounded-lg p-1">
          <FilterButton
            active={activeFilter === 'all'}
            onClick={() => setActiveFilter('all')}
          >
            All
          </FilterButton>
          <FilterButton
            active={activeFilter === 'active'}
            onClick={() => setActiveFilter('active')}
          >
            Active
          </FilterButton>
          <FilterButton
            active={activeFilter === 'inactive'}
            onClick={() => setActiveFilter('inactive')}
          >
            Inactive
          </FilterButton>
        </div>
      </div>

      {/* Definitions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full text-center py-8 text-muted-foreground">
            Loading...
          </div>
        ) : filteredDefinitions?.length === 0 ? (
          <div className="col-span-full text-center py-8 text-muted-foreground">
            No pipelines found
          </div>
        ) : (
          filteredDefinitions?.map((definition) => (
            <DefinitionCard
              key={definition.id}
              definition={definition}
              onRun={() => setRunModal(definition)}
            />
          ))
        )}
      </div>

      {/* Run Pipeline Modal */}
      {runModal && (
        <RunPipelineModal
          definition={runModal}
          onClose={() => setRunModal(null)}
        />
      )}
    </div>
  );
}

// Components

interface FilterButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function FilterButton({ active, onClick, children }: FilterButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
        active
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

interface DefinitionCardProps {
  definition: PipelineDefinition;
  onRun: () => void;
}

function DefinitionCard({ definition, onRun }: DefinitionCardProps) {
  const taskCount = definition.config?.tasks?.length ?? 0;

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden hover:border-primary/50 transition-colors">
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-secondary">
              <FileCode className="w-4 h-4 text-foreground" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">{definition.name}</h3>
              <span className="text-xs text-muted-foreground">v{definition.version}</span>
            </div>
          </div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            definition.is_active
              ? 'bg-emerald-500/20 text-emerald-400'
              : 'bg-zinc-500/20 text-zinc-400'
          }`}>
            {definition.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>

        {definition.description && (
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
            {definition.description}
          </p>
        )}

        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>{taskCount} tasks</span>
        </div>
      </div>

      <div className="px-4 py-3 bg-secondary/30 border-t border-border flex items-center gap-2">
        <button
          onClick={onRun}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Play className="w-3 h-3" />
          Run
        </button>
        <Link
          to="/hermes/builder"
          search={{ id: definition.id }}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
        >
          <Edit className="w-3 h-3" />
          Edit
        </Link>
        <button
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
        >
          <Copy className="w-3 h-3" />
          Clone
        </button>
      </div>
    </div>
  );
}

interface RunPipelineModalProps {
  definition: PipelineDefinition;
  onClose: () => void;
}

function RunPipelineModal({ definition, onClose }: RunPipelineModalProps) {
  const [inputData, setInputData] = useState('{}');
  const [error, setError] = useState<string | null>(null);
  const startPipeline = useStartPipeline();

  const handleRun = async () => {
    try {
      const parsed = JSON.parse(inputData);
      await startPipeline.mutateAsync({
        pipelineName: definition.name,
        inputData: parsed,
      });
      toast.success('Pipeline started');
      onClose();
    } catch (e) {
      if (e instanceof SyntaxError) {
        setError('Invalid JSON');
      } else {
        setError(e instanceof Error ? e.message : 'Failed to start pipeline');
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card rounded-lg border border-border shadow-xl w-full max-w-lg mx-4">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="font-semibold text-foreground">Run Pipeline: {definition.name}</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-secondary transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Input Data (JSON)
            </label>
            <textarea
              value={inputData}
              onChange={(e) => {
                setInputData(e.target.value);
                setError(null);
              }}
              rows={8}
              className="w-full px-3 py-2 text-sm font-mono rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
              placeholder='{ "key": "value" }'
            />
            {error && (
              <p className="mt-1 text-xs text-red-400">{error}</p>
            )}
          </div>

          <div className="flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="px-3 py-2 text-sm font-medium rounded-md hover:bg-secondary transition-colors"
            >
              Cancel
            </button>
            <ActionButton onClick={handleRun} variant="primary" size="md">
              <Play className="w-3.5 h-3.5" />
              Start Pipeline
            </ActionButton>
          </div>
        </div>
      </div>
    </div>
  );
}
